import { firebaseAuth, firebaseDatabase } from './firebase';

const entries = new Map();
let sessionUid;
let observingAuth = false;

export function clearCollectionCache() {
    const previous = [...entries.values()];
    entries.clear();
    previous.forEach(entry => {
        clearTimeout(entry.timer);
        entry.stop?.();
        entry.subscribers.forEach(({ error }) => error(new Error('Firebase session changed. Please sign in again.')));
    });
}

function checkSession() {
    const uid = firebaseAuth.currentUser?.uid || null;
    if (uid !== sessionUid) {
        sessionUid = uid;
        clearCollectionCache();
    }
    if (!observingAuth) {
        observingAuth = true;
        firebaseAuth.onAuthStateChanged(checkSession);
    }
}

export function subscribeCollection(name, next, error) {
    checkSession();
    let entry = entries.get(name);
    if (!entry) {
        entry = { subscribers: new Set(), snapshot: null, stop: null, timer: null };
        entries.set(name, entry);
    }
    clearTimeout(entry.timer);
    const subscriber = { next, error };
    entry.subscribers.add(subscriber);
    if (entry.snapshot) next(entry.snapshot);
    if (!entry.stop) {
        entry.stop = firebaseDatabase.collection(name).onSnapshot(snapshot => {
            if (entries.get(name) !== entry) return;
            entry.snapshot = snapshot;
            entry.subscribers.forEach(listener => listener.next(snapshot));
        }, failure => {
            if (entries.get(name) !== entry) return;
            entries.delete(name);
            clearTimeout(entry.timer);
            entry.stop?.();
            entry.subscribers.forEach(listener => listener.error(failure));
        });
    }
    let released = false;
    return () => {
        if (released) return;
        released = true;
        entry.subscribers.delete(subscriber);
        if (!entry.subscribers.size && entries.get(name) === entry) {
            entry.timer = setTimeout(() => {
                entry.stop?.();
                entries.delete(name);
            }, 30000);
        }
    };
}

export function getCachedCollection(name) {
    checkSession();
    const snapshot = entries.get(name)?.snapshot;
    return snapshot && !snapshot.metadata?.fromCache ? snapshot : null;
}

export function readCollection(name) {
    return new Promise((resolve, reject) => {
        let release;
        const finish = callback => value => {
            callback(value);
            Promise.resolve().then(() => release?.());
        };
        release = subscribeCollection(name, finish(resolve), finish(reject));
    });
}
